import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Accordion, Card } from '@ygtechnologies/rubus-ui';
import { MarkdownDisplay } from '@ygtechnologies/bygr-frontend-component-library';

import { ConfiguratorPresetDto } from '@/services/configurator';

import { PresetList } from './PresetList';
import { PresetApplyDialog } from './PresetApplyDialog';
import { PresetDetailsDialog } from './PresetDetailsDialog';
import { useOptionsScope } from '../OptionsScope';
import { useOptionConfigurator } from '../../OptionConfiguratorContext';
import type { PresetGroupWithContext } from '../useOptionsData';
import theme from './PresetCard.module.scss';

interface Props {
    presetGroup: PresetGroupWithContext;
    onSelectionChanged?: () => void;
}

export const PresetCard: React.FC<Props> = ({ presetGroup, onSelectionChanged }) => {
    const [applyTarget, setApplyTarget] = useState<ConfiguratorPresetDto | null>(null);
    const [detailsTarget, setDetailsTarget] = useState<ConfiguratorPresetDto | null>(null);

    const { group, deadlineId, roomId } = presetGroup;
    const { readOnly } = useOptionsScope();
    const { isShowroom } = useOptionConfigurator();
    // Block changes while a seller impersonates a buyer, and once the style group is signed/locked.
    // In the anonymous showroom there's no signing and `isLocked` only reflects an expired deadline,
    // which is irrelevant to the trial preview — so keep styles switchable there.
    const canChange = !readOnly && (isShowroom || !group.isLocked);

    return (
        <>
            <Accordion defaultOpen variant="elevated" title={<span className={theme.presetGroupTitle}>{group.name}</span>}>
                <div className={theme.presetGroupBody}>
                    <div className="preserve-white-space">
                        {group.description ? (
                            <MarkdownDisplay markdown={group.description} />
                        ) : (
                            <Trans>You can choose your style</Trans>
                        )}
                    </div>
                    <PresetList
                        readOnly={!canChange}
                        styles={group.styles}
                        onSelectClick={setApplyTarget}
                        onDetailsClick={setDetailsTarget}
                    />
                </div>
            </Accordion>
            <PresetApplyDialog
                show={!!applyTarget}
                style={applyTarget}
                deadlineId={deadlineId}
                roomId={roomId}
                canChange={canChange}
                onClose={() => setApplyTarget(null)}
                onApplied={onSelectionChanged}
            />
            <PresetDetailsDialog
                show={!!detailsTarget}
                style={detailsTarget}
                deadlineId={deadlineId}
                roomId={roomId}
                onClose={() => setDetailsTarget(null)}
            />
        </>
    );
};
